// utils.js
// Purpose: Utility functions for className merging
// Logic: Combines Tailwind CSS classes efficiently

import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import CryptoJS from "crypto-js";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function getPlanDetails(workspacePlanId = false) {
  if (workspacePlanId == true) {
    const selectedWorkspace = localStorage.getItem("selected_workspace");
    if (selectedWorkspace) {
      const parsedWorkspace = JSON.parse(selectedWorkspace);
      return parsedWorkspace?.user?.plan_id || null;
    }
    return null;
  }
  const logindata = localStorage.getItem("login_data");
  return logindata ? JSON.parse(logindata)?.resp_user?.plan_id : null;
}

export function getEncriptedLoginData(loginData) {
  if (loginData?.user_menus) {
    let encryptedUserMenus = CryptoJS.AES.encrypt(
      JSON.stringify(loginData?.user_menus),
      process.env.REACT_APP_PEXELS_API_KEY
    ).toString();
    return { ...loginData, user_menus: encryptedUserMenus };
  } else {
    return loginData;
  }
}

export function hasMenuAccess(menuKey) {
  const loginData = localStorage.getItem("login_data");
  if (!loginData) return false;

  const parsedData = JSON.parse(loginData);

  if (!parsedData?.user_menus) return false;

  let decryptedUserMenus;
  try {
    const bytes = CryptoJS.AES.decrypt(
      parsedData.user_menus,
      process.env.REACT_APP_PEXELS_API_KEY
    );
    decryptedUserMenus = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (error) {
    console.error("Error decrypting user menus:", error);
    return false;
  }

  if (menuKey == "social") return true; // temporary allow all access to social menu

  // temporary allow all access to monthly calendar, ai employee, chat, integration marketing
  if (
    menuKey == "monthly_calendar" ||
    menuKey == "ai_employee" ||
    menuKey == "chat" ||
    menuKey == "marketing"
  )
    return true;

  // if (!Array.isArray(decryptedUserMenus)) return false;
  // return decryptedUserMenus.includes(menuKey);
  // clog(decryptedUserMenus, "decryptedUserMenus");
  return decryptedUserMenus[menuKey] === true;
}

// helper function to check if subscription plan is greater than provided amount
export function isSubscriptionPlanGreaterThan(amount) {
  const loginData = localStorage.getItem("login_data");
  if (!loginData) return false;

  const parsedData = JSON.parse(loginData);
  const subscriptionPlan = Number(parsedData?.resp_user?.subscription?.amount);
  if (!subscriptionPlan) return false;
  return subscriptionPlan >= amount;
}

// helper function to get subscription plan amount
export function getSubscriptionPlanAmount() {
  const loginData = localStorage.getItem("login_data");
  if (!loginData) return 0;
  const parsedData = JSON.parse(loginData);
  return Number(parsedData?.resp_user?.subscription?.amount) || 0;
}

// Helper function to decrypt user menus from localStorage
export function getDecryptedUserMenus() {
  try {
    const loginData = localStorage.getItem("login_data");
    if (!loginData) {
      clog("No login_data found in localStorage");
      return {};
    }

    const parsedData = JSON.parse(loginData);
    if (!parsedData?.user_menus) {
      clog("No user_menus found in login_data");
      return {};
    }

    // Check if user_menus is already decrypted (for backward compatibility)
    if (
      typeof parsedData.user_menus === "object" &&
      !parsedData.user_menus.startsWith
    ) {
      clog("User menus appear to be already decrypted");
      return parsedData.user_menus;
    }

    // Decrypt the user_menus
    const bytes = CryptoJS.AES.decrypt(
      parsedData.user_menus,
      process.env.REACT_APP_PEXELS_API_KEY
    );
    const decryptedUserMenus = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

    clog("Successfully decrypted user menus:", decryptedUserMenus);
    return decryptedUserMenus;
  } catch (error) {
    console.error("Error decrypting user menus:", error);
    return {};
  }
}

export const menuKeyWithPath = [
  { script: "/script" },
  { user_activity: "/user-activity" },
  { person_level_id: "/person-level-id" },
  { company_level_id: "/company-level-id" },
  { email_outreach: "/email-outreach" },
  { knowledge_base: "/knowledge" },
  { monthly_calendar: "/monthly-calendar" },
  { ai_employee: "/ai-agent" },
  { marketing: "/integration-marketing" },
  { social: "/integration-social" },
  { sales: "/integration-sales" },
  { intent_topic: "/signals" },
];

// Custom localStorage setter that triggers events for cross-component updates
export function setLocalStorageWithEvent(key, value) {
  localStorage.setItem(key, value);
  // Dispatch custom event for same-tab detection
  window.dispatchEvent(
    new CustomEvent("localStorageUpdate", {
      detail: { key, value },
    })
  );
}

// Helper function to update user menus in login_data and trigger sidebar updates
export function updateUserMenusInLoginData(newUserMenus) {
  try {
    const loginData = localStorage.getItem("login_data");
    if (loginData) {
      const parsedData = JSON.parse(loginData);
      const updatedLoginData = {
        ...parsedData,
        user_menus: newUserMenus,
      };
      setLocalStorageWithEvent(
        "login_data",
        JSON.stringify(getEncriptedLoginData(updatedLoginData))
      );
      clog("User menus updated in localStorage:", newUserMenus);
    }
  } catch (error) {
    console.error("Error updating user menus in localStorage:", error);
  }
}

export function clog(...args) {
  if (process.env.REACT_APP_ENVIRONMENT === "development") {
    console.log(...args);
  }
}

// Helper function to check subscription status and force logout if expired
export function checkSubscriptionStatus() {
  try {
    const loginData = localStorage.getItem("login_data");
    if (!loginData) {
      return { isValid: true }; // No login data, let routing handle redirect
    }

    const parsedData = JSON.parse(loginData);
    const subscriptionStatus = parsedData?.resp_user?.subscription_status;
    const subscriptionExpireDate =
      parsedData?.resp_user?.subscription_expire_date;

    // If subscription is inactive (0)
    if (subscriptionStatus === 0) {
      // Check if expiration date has passed
      if (subscriptionExpireDate) {
        const currentDate = new Date();
        const expireDate = new Date(subscriptionExpireDate);

        // If current date is after expiration date, subscription has expired
        if (currentDate > expireDate) {
          clog("Subscription expired. Forcing logout.");
          return { isValid: false, reason: "expired" };
        }
      }
    }

    return { isValid: true };
  } catch (error) {
    console.error("Error checking subscription status:", error);
    return { isValid: true }; // Don't block on error
  }
}

// Helper function to perform force logout
export function forceLogout(navigate) {
  try {
    // Clear all authentication data
    localStorage.removeItem("login_token");
    localStorage.removeItem("login_data");
    localStorage.removeItem("workspaces_list");
    localStorage.removeItem("selected_workspace");
    localStorage.removeItem("selectedContentSettings");
    localStorage.removeItem("template_selected");
    localStorage.removeItem("assigning_template_to_file");
    localStorage.removeItem("selected_file");
    localStorage.removeItem("chat_details");
    localStorage.removeItem("chat_file");
    localStorage.removeItem("app_version"); // Clear app version

    clog("User logged out due to subscription expiration");

    // Redirect to login page
    if (navigate) {
      navigate("/");
    }
  } catch (error) {
    console.error("Error during force logout:", error);
  }
}

/**
 * Store redirect information in localStorage for persistence across page reloads
 * @param {Object} redirectInfo - Object containing redirect information { from, url }
 */
export function setRedirectAfterLogin(redirectInfo) {
  if (redirectInfo && (redirectInfo.from || redirectInfo.url)) {
    localStorage.setItem("redirect_after_login", JSON.stringify(redirectInfo));
  }
}

/**
 * Get redirect information from localStorage or location.state
 * @param {Object} locationState - Optional location.state from React Router
 * @returns {Object} Redirect information { from, url }
 */
export function getRedirectAfterLogin(locationState = null) {
  // First try to get from location.state
  if (locationState?.from || locationState?.url) {
    return locationState;
  }

  // Fallback to localStorage
  const stored = localStorage.getItem("redirect_after_login");
  return stored ? JSON.parse(stored) : {};
}

/**
 * Clear redirect information from localStorage
 */
export function clearRedirectAfterLogin() {
  localStorage.removeItem("redirect_after_login");
}

// util function to get boolean based on selected_workspace.user_type_id is user owner or not
export function isUserOwnerOfWorkspace() {
  try {
    const selectedWorkspace = localStorage.getItem("selected_workspace");
    if (selectedWorkspace) {
      const parsedWorkspace = JSON.parse(selectedWorkspace);
      return parsedWorkspace.user_type_id === 1; // Assuming 1 indicates owner
    }
    return false;
  } catch (error) {
    console.error("Error checking if user is owner of workspace:", error);
    return false;
  }
}

//function to get current workspace plan id
export function getCurrentWorkspacePlanId() {
  try {
    const selectedWorkspace = localStorage.getItem("selected_workspace");
    if (selectedWorkspace) {
      const parsedWorkspace = JSON.parse(selectedWorkspace);
      return parsedWorkspace?.user?.plan_id || null;
    }
    return null;
  } catch (error) {
    console.error("Error getting current workspace plan id:", error);
    return null;
  }
}